import React, { FC, useMemo, useState } from 'react';
import { TooltipWithBounds } from '@visx/tooltip';
import { formatDate, getDatesFromDuration, toDateRangeStr } from 'src/helpers/dates';
import { formatCurrencyValue } from 'src/models/procurements/Tender/helpers';
import { CumulativeTransaction, PeriodTransaction } from 'src/models/procurements/Billing/types';
import { getCumulativeValueByDate } from 'src/models/procurements/Billing/helpers';
import { getSupplierNameByOrgId } from 'src/models/organisations';
import { BarStackGroup, ChartContainer, DateRangeLine, XYChart } from 'src/common/Charts';
import { useChartTooltip } from 'src/common/Charts/hooks';
import { createColorScale } from 'src/common/Charts/helpers';
import HoverTooltip from './Tooltip';
import HoverTooltipCumulative from '../TooltipCumulative';
import { BillingChartProps } from '../types';
import MissingDataAlert from '../MissingDataAlert';
import styles from '../index.module.scss';
import { useTranslation } from 'react-i18next';

const BillingBarStackChart: FC<BillingChartProps> = ({
  data,
  vars,
  loading,
  error,
  children,
  className,
  displaySupplierLimits,
  onClick
}) => {
  const { t } = useTranslation();
  const {
    transactions = [],
    cumulativeTransactions = [],
    buyerCoverageGaps = [],
    currency,
    organisations: suppliers = []
  } = data ?? {};
  const { periodType: displayView = 'month', dateFrom, dateTo } = vars ?? {};
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } =
    useChartTooltip<PeriodTransaction>();
  const [cumulativeIndex, setCumulativeIndex] = useState<number | undefined>(undefined);
  const totalsByDate = useMemo(() => getCumulativeValueByDate(transactions), [transactions]);
  const yDomain: [number, number] = useMemo(() => [0, Math.max(...Object.values(totalsByDate))], [totalsByDate]);
  const xDomain = useMemo(
    () => (dateFrom && dateTo ? getDatesFromDuration([dateFrom, dateTo], displayView) : []),
    [dateFrom, dateTo, displayView]
  );
  const colorScale = useMemo(() => createColorScale(suppliers.map(s => s.organisationNumber)), [suppliers]);
  const desc = useMemo(() => (xDomain ? `(${toDateRangeStr(xDomain)})` : undefined), [xDomain]);

  return (
    <ChartContainer
      title={t('Tenders.Billing.BarStackChart.title')}
      desc={desc}
      className={className}
      loading={loading}
      error={error}
    >
      {({ width }) => (
        <>
          <MissingDataAlert buyerCoverageGaps={buyerCoverageGaps} displaySupplierLimits={displaySupplierLimits} />
          <XYChart
            width={width}
            height={640}
            xDomain={xDomain}
            yDomain={yDomain}
            colorScale={colorScale}
            formatX={date => formatDate(date, displayView, { showMonthName: true })}
            formatY={value => formatCurrencyValue(value as number)}
            formatLegend={key => getSupplierNameByOrgId(suppliers, key)}
          >
            <BarStackGroup<PeriodTransaction>
              data={transactions}
              getLegendKey={d => d.supplierOrgId}
              getYValue={d => d.amount}
              getXValue={d => d.period}
              onClick={onClick}
              offHover={hideTooltip}
              onHover={({ tooltipData, tooltipTop, tooltipLeft }) => {
                showTooltip({ tooltipData, tooltipTop, tooltipLeft });
              }}
            />
            <DateRangeLine<CumulativeTransaction>
              data={cumulativeTransactions}
              getDateRanges={d => [d.from, d.to]}
              offHover={hideTooltip}
              onHover={({ tooltipTop, tooltipLeft }, i) => {
                setCumulativeIndex(i);
                showTooltip({ tooltipTop, tooltipLeft });
              }}
            />
          </XYChart>
          {children}
          {tooltipOpen && (tooltipData || cumulativeIndex !== undefined) && (
            <TooltipWithBounds top={tooltipTop} left={tooltipLeft} className={styles.tooltip}>
              {tooltipData ? (
                <HoverTooltip
                  currency={currency}
                  displayView={displayView}
                  cumulativeValue={totalsByDate[tooltipData.period] ?? 0}
                  transaction={tooltipData}
                  colorScale={colorScale}
                  suppliers={suppliers}
                />
              ) : (
                cumulativeIndex !== undefined && (
                  <HoverTooltipCumulative
                    currency={currency}
                    data={cumulativeTransactions[cumulativeIndex]}
                    dataIndex={cumulativeIndex}
                  />
                )
              )}
            </TooltipWithBounds>
          )}
        </>
      )}
    </ChartContainer>
  );
};

export default BillingBarStackChart;
